import { TENANT_MODULE_LABELS, type TenantModuleKey } from "@loot-os/api-contract";
import { buttonVariants } from "@loot-os/ui/components/button";
import { SidebarTrigger } from "@loot-os/ui/components/sidebar";
import { skipToken, useQuery } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { ArrowRight, ExternalLink } from "lucide-react";

import { tenantCrumbs } from "../lib/breadcrumbs";
import { giftCodeBatchLabel } from "../lib/giftCodes";
import { primaryStorefrontHostname, storefrontUrl } from "../lib/storefrontUrl";
import { moduleForPathname, TENANT_NAV } from "../lib/tenantNav";
import { useTRPC } from "../lib/trpc";
import { useStandingNotice } from "../lib/useStandingNotice";
import { useTenantRole } from "../lib/useTenantRole";

/** Opens the active environment's storefront; falls back to a setup link while disabled. */
function ViewStorefrontLink({ tenantId }: { tenantId: string }) {
  const trpc = useTRPC();
  const storefront = useQuery(trpc.storefront.get.queryOptions());

  if (!storefront.data) return null;
  const hostname = primaryStorefrontHostname(storefront.data.domains);

  if (!storefront.data.config.enabled || !hostname) {
    return (
      <Link
        to="/tenants/$tenantId/storefront"
        params={{ tenantId }}
        className={buttonVariants({ variant: "outline", size: "sm" })}
      >
        Set up storefront
        <ArrowRight />
      </Link>
    );
  }
  return (
    <a
      href={storefrontUrl(hostname)}
      target="_blank"
      rel="noreferrer"
      title={hostname}
      className={buttonVariants({ variant: "outline", size: "sm" })}
    >
      <span className="max-w-56 truncate">{hostname}</span>
      <ExternalLink />
    </a>
  );
}

/**
 * What a page of a switched-off module shows in place of itself: reached only by a URL, since
 * the sidebar and the palette leave the page out.
 */
function ModuleOffNotice({ module, tenantId }: { module: TenantModuleKey; tenantId: string }) {
  const { isPlatformAdmin } = useTenantRole();
  return (
    <div className="flex flex-col items-start gap-3 rounded-md border px-4 py-6 text-sm">
      <p className="font-medium">{TENANT_MODULE_LABELS[module]} is not enabled for this tenant.</p>
      <p className="text-muted-foreground">
        {isPlatformAdmin
          ? "Turn it on under Settings › Modules."
          : "Ask Loot OS support if you need it."}
      </p>
      {isPlatformAdmin && (
        <Link
          to="/tenants/$tenantId/settings"
          params={{ tenantId }}
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          Open Settings
        </Link>
      )}
    </div>
  );
}

/**
 * Chrome for everything under /tenants/$tenantId: the breadcrumb trail down to the
 * current page and the storefront link; the environment row is in the sidebar. The route's
 * beforeLoad has already scoped the x-loot-tenant header before this renders.
 */
export function TenantLayout() {
  const { tenantId, boxId, userId, styleId, batchId } = useParams({ strict: false }) as {
    tenantId: string;
    boxId?: string;
    userId?: string;
    styleId?: string;
    batchId?: string;
  };
  const trpc = useTRPC();
  // me.tenants works for every role: members get their tenants, platform admins all.
  const tenants = useQuery(trpc.me.tenants.queryOptions());
  const tenant = tenants.data?.tenants.find((o) => o.id === tenantId);

  // The box editor's own query, shared through the cache — the deep crumb needs
  // the box's name, and asking for it here costs no extra request.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const box = useQuery(trpc.boxes.get.queryOptions(boxId ? { boxId } : skipToken));
  // Same for the customer page, whose crumb is the end user's external id.
  const endUser = useQuery(trpc.users.get.queryOptions(userId ? { endUserId: userId } : skipToken));
  // And the opener style editor, whose crumb is the style's name.
  const openerStyles = useQuery({
    ...trpc.openerStyles.list.queryOptions(),
    enabled: styleId !== undefined,
  });
  const openerStyle = openerStyles.data?.styles.find((s) => s.id === styleId);
  // And a gift code batch's page, whose crumb is its type and creation date.
  const giftCodeBatch = useQuery(
    trpc.giftCodes.batch.queryOptions(batchId ? { batchId } : skipToken),
  );
  const crumbs = tenantCrumbs(pathname, {
    ...(box.data ? { [box.data.id]: box.data.name } : {}),
    ...(endUser.data ? { [endUser.data.user.id]: endUser.data.user.externalId } : {}),
    ...(openerStyle ? { [openerStyle.id]: openerStyle.name } : {}),
    ...(giftCodeBatch.data
      ? { [giftCodeBatch.data.batch.id]: giftCodeBatchLabel(giftCodeBatch.data.batch) }
      : {}),
  });

  // Only platform admins ever see an archived tenant here (me.tenants hides it from
  // members), so this is staff being reminded that everything is frozen.
  const navigate = useNavigate();
  const archived = Boolean(tenant?.archivedAt);
  useStandingNotice(archived, {
    id: "tenant-archived",
    message:
      "This tenant is archived: storefronts are dark, API keys are refused and members are locked out. Restore it to make changes.",
    action: {
      label: "Manage",
      onClick: () => void navigate({ to: "/tenants/$tenantId/settings", params: { tenantId } }),
    },
  });

  // The opens pause is tenant-wide, so its notice is raised here and holds on every page of
  // every environment: a paused tenant must not read as a quiet one from Overview or Boxes.
  // Keyed on the saved setting, which is what the API is doing right now; the Settings page's
  // own draft of the switch changes nothing until saved. Same query the Settings page runs.
  // Sticky, like the platform-switch notice: a kill switch the reader has to keep in mind, and
  // one most members cannot flip back. Raised only once the role is known, because the action
  // is read at raise time and a toast raised before it resolved would keep its missing button.
  const detail = useQuery(trpc.tenant.get.queryOptions());
  const { role, can } = useTenantRole();
  useStandingNotice(role !== null && detail.data?.settings.opensPaused === true, {
    id: "opens-paused",
    message: "Opens are paused — every open request returns opens_paused until you resume.",
    duration: Number.POSITIVE_INFINITY,
    // Settings is an admin's page and the sidebar hides it below that, so the toast offers the
    // way there to an admin alone.
    action: can("admin")
      ? {
          label: "Manage",
          onClick: () => void navigate({ to: "/tenants/$tenantId/settings", params: { tenantId } }),
        }
      : undefined,
  });

  // A page of a module platform staff switched off: the notice instead of the page, whose
  // writes the server would refuse anyway.
  const pageModule = moduleForPathname(TENANT_NAV, pathname, tenantId);
  const moduleOff = pageModule !== null && detail.data?.modules[pageModule] === false;

  // Deleted tenant or a typo'd deep link — nothing to manage here.
  if (tenants.isSuccess && !tenant) return <Navigate to="/" />;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm">
          <SidebarTrigger className="-ml-1.5" />
          <Link to="/" className="text-muted-foreground hover:underline">
            Tenants
          </Link>
          <span className="text-muted-foreground">/</span>
          {/* The tenant is the last crumb on its overview, a link once you go deeper. */}
          {crumbs.length === 0 ? (
            <span className="font-semibold">{tenant?.name ?? "…"}</span>
          ) : (
            <Link
              to="/tenants/$tenantId"
              params={{ tenantId }}
              className="text-muted-foreground hover:underline"
            >
              {tenant?.name ?? "…"}
            </Link>
          )}
          {crumbs.map((crumb, i) => {
            const last = i === crumbs.length - 1;
            return (
              <span key={crumb.path} className="flex items-center gap-2">
                <span className="text-muted-foreground">/</span>
                {last || !crumb.to ? (
                  <span className="font-semibold">{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.to}
                    params={{ tenantId }}
                    className="text-muted-foreground hover:underline"
                  >
                    {crumb.label}
                  </Link>
                )}
              </span>
            );
          })}
        </nav>
        <div className="flex items-center gap-2">
          {/* An archived tenant's storefront 404s, so the link would only mislead. */}
          {!archived && detail.data?.modules.storefront !== false && (
            <ViewStorefrontLink tenantId={tenantId} />
          )}
        </div>
      </header>
      {moduleOff && pageModule ? (
        <ModuleOffNotice module={pageModule} tenantId={tenantId} />
      ) : (
        <Outlet />
      )}
    </div>
  );
}
